import { Component, Input } from '@angular/core';
import { AbstractComponent } from '../../shared/components/abstract-component/abstract.component';
import { ListLoader } from '../../shared/components/list-loader/list-loader';
import { RootBelastungVm } from '../../core/services/grundpfandrecht-viewmodel.service';
import { TableSortModel } from '../../shared/tablesort/tableSortModel';
import { LinkComponent, NavTarget } from '../../shared/components/link/link.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatumPipe } from '../../shared/pipes/simple/datum.pipe';
import { ComparatorService } from '../../shared/services/comparator-service';
import { keysToTranslate } from '../../../i18n/keys-to-translate';
import {
  CapititelEntryMeta,
  PersonStammMasterListEntryMeta,
  PfandrechtMasterDetailEntryMeta,
  PfandrechtVersionDetailEntryMeta
} from '../../core/gb-api';
import { BetragPipe } from '../../shared/pipes/simple/betrag.pipe';
import { KompaktStandardrechtComponent } from '../../shared/kompaktdarstellung/standardrecht/kompakt-standardrecht.component';
import { KompaktGrundstueckComponent } from '../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HistoryDirective } from '../../shared/directives/history.directive';
import { KompaktPersongbComponent } from '../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import { KompaktTimepointComponent } from '../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { OrderByGlaeubigerSchuldnerMasterPipe } from '../../shared/pipes/order/order-by-glaeubigerSchuldnerMaster.pipe';
import { GlaeubigerRolleCodePipe } from '../../shared/pipes/simple/glaeubiger-rolle-code.pipe';
import { OrderByPfandrechtBelastungMasterPipe } from '../../shared/pipes/order/order-by-pfandrechtBelastungMaster.pipe';
import { ReadMoreComponent } from '../../shared/widgets/read-more/read-more.component';
import { TranslateNamePipe } from '../../shared/pipes/simple/translate-name.pipe';
import { FormatRechteIdPipe } from '../../shared/pipes/format/format-rechte-id.pipe';
import { OrderByPfandrechtBelastungPipe } from '../../shared/pipes/order/order-by-pfandrecht-belastung.pipe';
import { ListLoaderWrapperComponent } from '../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { KompaktBelegIcpComponent } from '../shared/component/kompakt-beleg-icp/kompakt-beleg-icp.component';
import { RechtsgrundIcpComponent } from '../shared/component/rechtsgrund-icp/rechtsgrund-icp.component';
import { KompaktGrundpfandrechtIcpComponent } from '../shared/component/kompakt-grundpfandrecht-icp/kompakt-grundpfandrecht-icp.component';
import PfandtitelStatusEnum = CapititelEntryMeta.PfandtitelStatusEnum;

@Component({
  selector: 'cap-grundpfandrecht-table-icp',
  imports: [
    BetragPipe,
    KompaktStandardrechtComponent,
    KompaktGrundstueckComponent,
    TranslatePipe,
    NgClass,
    HistoryDirective,
    KompaktPersongbComponent,
    KompaktTimepointComponent,
    OrderByGlaeubigerSchuldnerMasterPipe,
    GlaeubigerRolleCodePipe,
    DatumPipe,
    OrderByPfandrechtBelastungMasterPipe,
    ReadMoreComponent,
    TranslateNamePipe,
    FormatRechteIdPipe,
    LinkComponent,
    NgTemplateOutlet,
    OrderByPfandrechtBelastungPipe,
    ListLoaderWrapperComponent,
    KompaktBelegIcpComponent,
    RechtsgrundIcpComponent,
    KompaktGrundpfandrechtIcpComponent
  ],
  templateUrl: './grundpfandrecht-table-icp.component.html'
})
export class GrundpfandrechtTableIcpComponent extends AbstractComponent {
  @Input()
  hasRight: boolean = true;
  @Input()
  isEigentuemer: boolean = false;

  private _listLoader: ListLoader<RootBelastungVm>;
  private sortModel: TableSortModel<RootBelastungVm>;
  navTargetGrundstuecke: NavTarget = NavTarget.NAV_GRUNDSTUECK_ES;

  constructor(
    protected translateService: TranslateService,
    private datumPipe: DatumPipe,
    protected comparatorService: ComparatorService
  ) {
    super();
    keysToTranslate<string>([
      'Pfandstelle',
      'DatumOderBelegOderEntstehung',
      'ID-DatumOderBelegOderRechtsgrund',
      'glaeubiger',
      'Nachrueckungsrecht',
      'Mit',
      'Gruppe',
      'detailPfandrechtGlaeubiger',
      'gehoertZu'
    ]);
    this.sortModel = new TableSortModel('table', this.comparatorService);
    this.sortModel.addColumn('dummyColumn', this.comparatorService.pfandrechtBelastungComparator).switchSortDirection();
  }

  @Input()
  set listLoader(value: ListLoader<RootBelastungVm>) {
    if (!value) {
      return;
    }
    this._listLoader = value;
    this._listLoader.applySortModel(this.sortModel);
  }

  get listLoader(): ListLoader<RootBelastungVm> {
    return this._listLoader;
  }

  getQueryParams(master: PfandrechtMasterDetailEntryMeta, version: PfandrechtVersionDetailEntryMeta) {
    return { id: master.id.id, versionId: version.id.id, histEnum: version.histInfo.histEnum };
  }

  getTiteleingang(titel: CapititelEntryMeta): string {
    let text: string;
    const anmelder: string = this.getAnmelder(titel.anmelder);
    const eintragdatum = this.datumPipe.transform(titel.eintragDatum);
    if (titel.pfandtitelStatus === PfandtitelStatusEnum.ERSTELLT || titel.pfandtitelStatus === PfandtitelStatusEnum.EINGETRAGEN) {
      if (anmelder) {
        text = this.translateService.instant('JaAmDatumDurchAnmelder', { datum: eintragdatum, anmelder: anmelder });
      } else {
        text = this.translateService.instant('JA');
      }
    } else {
      text = this.translateService.instant('NEIN');
    }
    return text;
  }

  private getAnmelder(anmelder: Array<PersonStammMasterListEntryMeta>): string {
    return anmelder
      .map((a): string => {
        const vorname: string = a.version.vorname;
        const sitz: string = a.version.sitz;
        const name: string[] = vorname ? [a.version.name, vorname] : [a.version.name];
        if (sitz) {
          return [name.join(' '), sitz].join(', ');
        } else {
          return name.join(' ');
        }
      })
      .join(', ');
  }
}
